import { ExternalLink, AlertCircle } from "lucide-react";
import { normalizeLink } from "@/lib/normalizeUrl";

// Shared between the Applications Queue (Ticket 3) and the Interview page (Ticket 4) —
// renders a candidate's submitted work (youtube/vimeo/instagram/behance/website/work_links)
// as embeds + titled link cards, purely for reference/review. Deliberately never a stored
// field ("Work Links Reviewed" was explicitly NOT added as a Notion property per the
// founder's instruction — this is display-only).
//
// Intelligent Work-Link Resolver (2026-08) — every value goes through `normalizeLink`
// (@/lib/normalizeUrl.js): it percent-decodes, extracts a URL embedded after descriptive text
// (keeping that text as the card label), expands platform handles, and returns resolved:false
// for anything it can't turn into a real URL — those render as a flagged, non-clickable note
// so the reviewer eyeballs them (never a broken/encoded href).
function youtubeId(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url) {
  const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function LinkCard({ url, label }) {
  let host = url;
  try { host = new URL(url).hostname.replace(/^www\./, ""); } catch {}
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`work-link-card-${label.toLowerCase()}`}
      className="flex items-center gap-2 rounded-md border border-hairline bg-paper px-3 py-2.5 text-sm text-ink hover:border-brand/50 transition-colors">
      <ExternalLink className="h-3.5 w-3.5 text-faint shrink-0" />
      <span className="font-medium">{label}</span>
      <span className="text-faint text-xs truncate">{host}</span>
    </a>
  );
}

function UnresolvedCard({ label, raw }) {
  return (
    <div data-testid={`work-link-unresolved-${label.toLowerCase()}`}
      className="flex items-start gap-2 rounded-md border border-dashed border-hairline bg-paper px-3 py-2.5 text-sm">
      <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="font-medium text-ink">{label}</div>
        <div className="text-faint text-xs break-words">{raw}</div>
        <div className="text-amber-600 text-[11px] mt-0.5">link couldn't be auto-resolved. Check manually</div>
      </div>
    </div>
  );
}

export function WorkLinksPreview({ candidate }) {
  const embeds = [];
  const cards = [];
  const unresolved = [];

  const addTyped = (raw, fieldType, label) => {
    if (!raw) return;
    const { href, resolved } = normalizeLink(raw, fieldType);
    if (resolved) cards.push({ url: href, label });
    else unresolved.push({ label, raw: String(raw) });
  };

  const youtube = normalizeLink(candidate.youtube, "youtube");
  const yt = youtube.resolved ? youtubeId(youtube.href) : null;
  if (yt) embeds.push({ key: "youtube", id: yt, kind: "youtube" });
  else addTyped(candidate.youtube, "youtube", "YouTube");
  const vimeo = normalizeLink(candidate.vimeo, "vimeo");
  const vm = vimeo.resolved ? vimeoId(vimeo.href) : null;
  if (vm) embeds.push({ key: "vimeo", id: vm, kind: "vimeo" });
  else addTyped(candidate.vimeo, "vimeo", "Vimeo");
  addTyped(candidate.instagram, "instagram", "Instagram");
  addTyped(candidate.behance, "behance", "Behance");
  addTyped(candidate.website, null, "Website");

  (candidate.work_links || "").split(/[\n,;]/).map((s) => s.trim()).filter(Boolean).forEach((raw, i) => {
    const { href, label, resolved } = normalizeLink(raw);
    if (!resolved) { unresolved.push({ label: `Work Link ${i + 1}`, raw }); return; }
    const y = youtubeId(href), v = vimeoId(href);
    if (y) embeds.push({ key: `wl-yt-${i}`, id: y, kind: "youtube" });
    else if (v) embeds.push({ key: `wl-vm-${i}`, id: v, kind: "vimeo" });
    else cards.push({ url: href, label: label || `Work Link ${cards.filter((c) => c.label.startsWith("Work Link")).length + 1}` });
  });

  if (!embeds.length && !cards.length && !unresolved.length) {
    return <div className="text-sm text-faint italic" data-testid="work-links-preview-empty">No work links on file.</div>;
  }
  return (
    <div className="space-y-3" data-testid="work-links-preview">
      {embeds.map((e) => (
        <div key={e.key} className="aspect-video w-full overflow-hidden rounded-md border border-hairline bg-black">
          <iframe
            title={e.kind}
            src={e.kind === "youtube" ? `https://www.youtube.com/embed/${e.id}` : `https://player.vimeo.com/video/${e.id}`}
            className="h-full w-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
          />
        </div>
      ))}
      {(cards.length > 0 || unresolved.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {cards.map((c, i) => <LinkCard key={`c${i}`} url={c.url} label={c.label} />)}
          {unresolved.map((u, i) => <UnresolvedCard key={`u${i}`} label={u.label} raw={u.raw} />)}
        </div>
      )}
    </div>
  );
}

